import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShoppingBag, Award, Loader2, Save, Sliders, Crown, Users } from "lucide-react";

export default function AdminChannels() {
  const [policies, setPolicies] = useState([]);
  const [managers, setManagers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [feeSummary, setFeeSummary] = useState(null);

  const load = async () => {
    const [p, m, f] = await Promise.all([
      api.get("/channels/policies"),
      api.get("/admin/managers"),
      api.get("/finance/fee-ledger"),
    ]);
    setPolicies(p.data?.policies || []);
    setManagers(m.data?.managers || m.data || []);
    setFeeSummary(f.data?.summary);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const updatePolicy = async (channel, patch) => {
    setBusy(channel);
    try {
      const r = await api.put(`/channels/policies/${channel}`, patch);
      toast.success("Policy aggiornata");
      setPolicies((arr) => arr.map((p) => (p.channel === channel ? r.data : p)));
    } catch (e) {
      toast.error("Errore aggiornamento");
    } finally {
      setBusy(null);
    }
  };

  const toggleInnerCircle = async (mgrId, value) => {
    try {
      await api.put(`/admin/managers/${mgrId}/inner-circle`, { inner_circle: value });
      toast.success("Aggiornato");
      load();
    } catch (e) {
      // Backend route may not exist — fallback: update via /admin/managers/{id}
      try {
        await api.put(`/admin/managers/${mgrId}`, { inner_circle: value });
        toast.success("Aggiornato");
        load();
      } catch (err) {
        toast.error("Endpoint admin/managers PUT mancante");
      }
    }
  };

  const runBackfill = async () => {
    setBusy("backfill");
    try {
      const r = await api.post("/channels/migrate-backfill");
      toast.success(`Migration completata: ${JSON.stringify(r.data?.stats)}`);
    } catch (e) {
      toast.error("Errore migration");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
        <Sliders size={24} className="text-[#2D6BFF]" /> Configurazione Canali
      </h1>
      <p className="mt-1 text-slate-600 mb-6">Gestisci le policy del modello ibrido Marketplace + Agency Premium.</p>

      <Tabs defaultValue="policies" data-testid="channels-tabs">
        <TabsList>
          <TabsTrigger value="policies" data-testid="tab-policies"><Sliders size={14} className="mr-1.5" /> Policy</TabsTrigger>
          <TabsTrigger value="managers" data-testid="tab-managers"><Users size={14} className="mr-1.5" /> Manager</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><Crown size={14} className="mr-1.5" /> Fee Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="mt-5 space-y-4">
          {policies.length === 0 ? (
            <div className="cxo-card p-8 text-center text-slate-500">
              <Loader2 className="animate-spin mx-auto" />
              <p className="mt-2 text-sm">Caricamento policy...</p>
            </div>
          ) : (
            policies.map((p) => (
              <PolicyCard key={p.channel} policy={p} onSave={updatePolicy} busy={busy === p.channel} />
            ))
          )}

          <div className="cxo-card p-5">
            <h3 className="font-semibold text-slate-900">Migration / Backfill</h3>
            <p className="text-sm text-slate-600 mt-1">Esegui questa azione solo se hai aggiunto record antichi senza il campo channel.</p>
            <Button onClick={runBackfill} disabled={busy === "backfill"} variant="outline" className="mt-3 gap-1.5" data-testid="backfill-btn">
              {busy === "backfill" && <Loader2 className="animate-spin" size={14} />}
              Esegui backfill (idempotente)
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="managers" className="mt-5">
          <div className="cxo-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3 text-left">Manager</th>
                  <th className="p-3 text-left">Ruolo</th>
                  <th className="p-3 text-left">Channels</th>
                  <th className="p-3 text-center">Inner Circle</th>
                  <th className="p-3 text-center">Marketplace auto-apply</th>
                </tr>
              </thead>
              <tbody>
                {managers.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500">Nessun manager.</td></tr>}
                {managers.slice(0, 50).map((m) => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="p-3">
                      <p className="font-medium text-slate-900">{m.user?.full_name || m.full_name || "—"}</p>
                      <p className="text-xs text-slate-500">{m.city || "—"}</p>
                    </td>
                    <td className="p-3 text-slate-700">{m.primary_role || "—"}</td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        {(m.channels_enabled || []).map((c) => (
                          <Badge key={c} className={c === "agency" ? "bg-[#FFF6E6] text-[#B7791F] border-0 text-[10px]" : "bg-[#EAF0FF] text-[#2D6BFF] border-0 text-[10px]"}>
                            {c === "agency" ? <Award size={10} className="mr-1" /> : <ShoppingBag size={10} className="mr-1" />}
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <Switch
                        checked={!!m.inner_circle}
                        onCheckedChange={(v) => toggleInnerCircle(m.id, v)}
                        data-testid={`inner-circle-toggle-${m.id}`}
                      />
                    </td>
                    <td className="p-3 text-center">
                      <Badge className={m.marketplace_auto_apply ? "bg-[#E9F8F1] text-[#1F9D6A] border-0" : "bg-slate-100 text-slate-600 border-0"}>
                        {m.marketplace_auto_apply ? "Sì" : "No"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="mt-5">
          {feeSummary && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
              <Stat label="Gross totale" value={`€${feeSummary.total_gross.toLocaleString("it-IT")}`} />
              <Stat label="Share CXOORA" value={`€${feeSummary.total_cxoora_share.toLocaleString("it-IT")}`} color="text-[#1F9D6A]" />
              <Stat label="Share manager" value={`€${feeSummary.total_manager_share.toLocaleString("it-IT")}`} color="text-[#2D6BFF]" />
            </div>
          )}
          {feeSummary && feeSummary.by_channel && (
            <div className="cxo-card p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Ripartizione per canale</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(feeSummary.by_channel).map(([ch, val]) => (
                  <div key={ch} className="rounded-lg bg-slate-50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={ch === "agency" ? "bg-[#FFF6E6] text-[#B7791F] border-0" : "bg-[#EAF0FF] text-[#2D6BFF] border-0"}>
                        {ch === "agency" ? <Award size={12} className="mr-1" /> : <ShoppingBag size={12} className="mr-1" />}
                        {ch}
                      </Badge>
                      <span className="text-xs text-slate-500">({val.count} fatture)</span>
                    </div>
                    <p className="text-sm text-slate-600">Gross: <strong>€{val.gross.toLocaleString("it-IT")}</strong></p>
                    <p className="text-sm text-[#1F9D6A]">CXOORA: <strong>€{val.cxoora.toLocaleString("it-IT")}</strong></p>
                    <p className="text-sm text-[#2D6BFF]">Manager: <strong>€{val.manager.toLocaleString("it-IT")}</strong></p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value, color = "text-slate-900" }) => (
  <div className="cxo-card p-3">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 font-display text-xl font-bold cxo-tabular ${color}`}>{value}</p>
  </div>
);

const PolicyCard = ({ policy, onSave, busy }) => {
  const [draft, setDraft] = useState(policy);
  useEffect(() => setDraft(policy), [policy]);
  const isAgency = draft.channel === "agency";
  return (
    <div className="cxo-card p-5" data-testid={`policy-card-${draft.channel}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`rounded-xl p-2 ${isAgency ? "bg-[#FFF6E6] text-[#B7791F]" : "bg-[#EAF0FF] text-[#2D6BFF]"}`}>
            {isAgency ? <Award size={18} /> : <ShoppingBag size={18} />}
          </span>
          <div>
            <h3 className="font-display font-bold text-slate-900">{isAgency ? "Agency Premium" : "Marketplace"}</h3>
            <p className="text-xs text-slate-500">Policy del canale</p>
          </div>
        </div>
        <Button onClick={() => onSave(draft.channel, draft)} disabled={busy} className="gap-1.5" data-testid={`save-policy-${draft.channel}`}>
          {busy && <Loader2 className="animate-spin" size={14} />}
          <Save size={14} /> Salva
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Field label="Score min. auto-shortlist"><Input type="number" max="100" value={draft.min_score_threshold || 0} onChange={(e) => setDraft({ ...draft, min_score_threshold: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Max manager in shortlist"><Input type="number" value={draft.max_managers_in_shortlist || 0} onChange={(e) => setDraft({ ...draft, max_managers_in_shortlist: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Fee % (marketplace)"><Input type="number" step="0.5" value={draft.default_fee_pct || 0} onChange={(e) => setDraft({ ...draft, default_fee_pct: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Margin % (agency)"><Input type="number" step="0.5" value={draft.default_margin_pct || 0} onChange={(e) => setDraft({ ...draft, default_margin_pct: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Soglia budget (€/mese)"><Input type="number" value={draft.monthly_budget_threshold || 0} onChange={(e) => setDraft({ ...draft, monthly_budget_threshold: parseFloat(e.target.value) || 0 })} /></Field>
        <div className="flex items-center gap-3 pt-5">
          <Switch checked={!!draft.requires_admin_curation} onCheckedChange={(v) => setDraft({ ...draft, requires_admin_curation: v })} />
          <Label className="text-sm">Curation admin obbligatoria</Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!draft.allow_direct_messaging} onCheckedChange={(v) => setDraft({ ...draft, allow_direct_messaging: v })} />
          <Label className="text-sm">Messaggi diretti consentiti</Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!draft.nda_required} onCheckedChange={(v) => setDraft({ ...draft, nda_required: v })} />
          <Label className="text-sm">NDA obbligatorio</Label>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs mb-1 block">{label}</Label>
    {children}
  </div>
);
